// "Preparar": cut a bought vegetable into bags (for soup), as Android's PrepareActions.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import * as api from '../plannerApi';
import * as T from '../types';
import { number, quantity } from '../labels';

/** One tap of − / + for what is used: one unit, 100 g / 100 ml, or a quarter of a kg / litre. */
function usedStep(unit: T.MeasureUnit): number {
  if (unit === 'Gram' || unit === 'Millilitre') return 100;
  if (unit === 'Kilogram' || unit === 'Litre') return 0.25;
  return 1;
}

function Stepper({ label, detail, value, unit, step, onChange }: {
  label: string; detail?: string | null; value: number; unit: T.MeasureUnit; step: number; onChange: (value: number) => void;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 52 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography>{label}</Typography>
        {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
      </Box>
      <Stack direction="row" sx={{ alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 5, flexShrink: 0 }}>
        <IconButton size="small" color="primary" aria-label={`Menos: ${label}`} onClick={() => onChange(Math.max(step, value - step))}><RemoveIcon fontSize="small" /></IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 44, textAlign: 'center', whiteSpace: 'nowrap' }}>{quantity(value, unit)}</Typography>
        <IconButton size="small" color="primary" aria-label={`Mais: ${label}`} onClick={() => onChange(value + step)}><AddIcon fontSize="small" /></IconButton>
      </Stack>
    </Box>
  );
}

/** What was said in the step, for a caller that saves it its own way (putting a trip away). */
export interface PrepareSaid { preparation: T.Preparation; used: number; bags: number; locationPublicId: T.Guid | null; }

/**
 * For a vegetable (its bag; which one when there are several) or the bag itself: how many were used and how many bags
 * came out, both starting from the average of the last times, and where they go (the freezer by default).
 * Putting a trip away (`tripItemId`), what was bought is suggested as used and `save` replaces preparing at home.
 */
export function PrepareDialog({ itemId, tripItemId, save, onClose, onDone }: {
  itemId: T.Guid | null; tripItemId?: T.Guid; save?: (said: PrepareSaid) => Promise<string>; onClose: () => void; onDone: (message: string) => void;
}) {
  const [preparations, setPreparations] = useState<T.Preparation[] | null>(null);
  const [locations, setLocations] = useState<T.StorageLocation[]>([]);
  const [chosen, setChosen] = useState('');
  const [used, setUsed] = useState(1);
  const [bags, setBags] = useState(1);
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!itemId) return;
    setPreparations(null);
    setError(null);
    Promise.all([api.getPreparations(itemId, tripItemId), api.getStorageLocations()])
      .then(([list, places]) => {
        setPreparations(list);
        setLocations(places);
        if (list.length > 0) setChosen(list[0].itemPublicId);
      })
      .catch((e: any) => setError(e.message ?? 'Ocorreu um erro.'));
  }, [itemId, tripItemId]);

  const preparation = preparations?.find(p => p.itemPublicId === chosen);
  useEffect(() => {
    if (!preparation) return;
    setUsed(preparation.suggestedUsed);
    setBags(preparation.suggestedBags);
    setLocation(preparation.locationPublicId ?? '');
  }, [preparation]);

  const submit = async () => {
    if (!preparation) return;
    setBusy(true);
    setError(null);
    try {
      if (save) {
        onDone(await save({ preparation, used, bags, locationPublicId: location || null }));
      } else {
        await api.prepare(preparation.itemPublicId, { used, bags, usedUnit: preparation.sourceUnit, locationPublicId: location || null });
        onDone(`${quantity(bags, preparation.unit)} de ${preparation.name}`);
      }
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={itemId !== null} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{preparation ? `Preparar ${preparation.name}` : 'Preparar'}</DialogTitle>
      <DialogContent>
        {preparations && preparations.length === 0 && <Alert severity="info">Este artigo não tem nada que se prepare em casa.</Alert>}
        {preparation && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Diz quantos usaste e quantos sacos saíram: o legume sai do stock e os sacos entram.
            </Typography>
            {preparations!.length > 1 && (
              <TextField select size="small" label="Preparar" value={chosen} onChange={e => setChosen(e.target.value)}>
                {preparations!.map(p => <MenuItem key={p.itemPublicId} value={p.itemPublicId}>{p.name}</MenuItem>)}
              </TextField>
            )}
            <Stepper
              label={`${preparation.sourceName} usado`}
              detail={preparation.sourceQuantity !== null && preparation.sourceQuantity !== undefined ? `tens ${quantity(preparation.sourceQuantity, preparation.sourceUnit)}` : null}
              value={used} unit={preparation.sourceUnit} step={usedStep(preparation.sourceUnit)} onChange={setUsed}
            />
            <Stepper
              label="Sacos"
              detail={preparation.bagsPerUnit === 1 ? null : `em média ${number(preparation.bagsPerUnit, 1)} por unidade`}
              value={bags} unit={preparation.unit} step={1} onChange={setBags}
            />
            <TextField select size="small" label="Onde ficam" value={location} onChange={e => setLocation(e.target.value)} sx={{ mt: 1 }}>
              {locations.map(l => <MenuItem key={l.publicId} value={l.publicId}>{l.fullName}</MenuItem>)}
            </TextField>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!preparation || busy}>Preparar</Button>
      </DialogActions>
    </Dialog>
  );
}
